import { EventInput } from '@fullcalendar/core';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Avatar, Button, Loading } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import LeadChatLink from '../../../../../components/ChatModal/LeadChatLink.component';
import useOpenLeadChat, { toLeadChatModalLead } from '../../../../../components/ChatModal/useOpenLeadChat.hook';
import { useUserContext } from '../../../../../providers/UserProvider/User.context';
import { GetInitialsName } from '../../../../../utils/GetInitialsName';
import { eAgendaTabs, eTemplateTypeColors } from '../../../Agenda.enums';
import { resolveLeadLabel } from '../Calendar.functions';
import { useCalendarContext } from '../Calendar.hook';
import { EventContentProps } from '../Calendar.types';
import './EventContent.styles.scss';

export function EventContent({ arg, selectedEvent }: EventContentProps) {
  const { t } = useTranslation();
  const {
    setShowDeleteEvent,
    setShowUpdateEvent,
    setCurrentArg,
    sendEventReminder,
    currentTab,
    templates,
  } = useCalendarContext();
  const openLeadChat = useOpenLeadChat();
  const { currentUser } = useUserContext();

  const [shouldShowReminderButton, setShouldShowReminderButton] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const event = Object.assign({}, selectedEvent, arg.event.toPlainObject());
  const isEventDateAfterCurrent = moment(new Date()).isAfter(event.end);

  useEffect(() => {
    const eventGuests: string[] = event.extendedProps?.data?.guests;
    const guestWithoutCurrentUser = eventGuests?.filter(
      (guest) => guest !== currentUser?.authId,
    );

    const eventHasGuests =
      guestWithoutCurrentUser && guestWithoutCurrentUser.length > 0;
    setShouldShowReminderButton(eventHasGuests);
  }, [event?.extendedProps?.data.guests, currentUser?.authId]);

  if (!event) return null;

  const data = event.extendedProps?.data || {};

  if (!data || !data.title) return null;

  function getTemplateNameType(): string {
    const template = templates?.find(
      (template) => template.id === data.templateId,
    );

    return (
      `${template?.emoji && String.fromCodePoint(parseInt(template.emoji, 16))} ${template?.name}` ||
      ''
    );
  }

  const eventTemplateColor = templates?.find(
    (template) => template.id === data.templateId,
  );

  function eventDetail(): string {
    switch (eventTemplateColor?.event_type) {
      case 'EVENT_TYPE_CALL':
        return `bg-agenda-event-${eTemplateTypeColors.EVENT_TYPE_CALL}`;
      case 'EVENT_TYPE_MEETING':
        return `bg-agenda-event-${eTemplateTypeColors.EVENT_TYPE_MEETING}`;
      case 'EVENT_TYPE_TASK':
        return `bg-agenda-event-${eTemplateTypeColors.EVENT_TYPE_TASK}`;
      default:
        return 'bg-gray-100';
    }
  }

  function eventTypeColor(): string {
    if (isEventDateAfterCurrent) {
      return 'bg-neutral/40 line-through text-neutral-content';
    }

    if (currentTab === eAgendaTabs.EVENTS) {
      return 'bg-neutral/10';
    }

    return eventDetail();
  }

  async function sendReminder(event: EventInput) {
    setIsLoading(true);

    await sendEventReminder(event);

    setIsLoading(false);
  }

  const leadId = data?.lead?.id;

  const leadDisplay = resolveLeadLabel({
    relatedEntityId: data?.related_entity_id,
    leadId: data?.lead?.id,
    leadFullName: data?.lead?.full_name,
  });

  function openChat() {
    if (leadId) openLeadChat(toLeadChatModalLead({ leadId }));
  }

  const leadLabel = (
    <>
      <Avatar
        letters={GetInitialsName(
          leadDisplay.kind === 'name' ? leadDisplay.value : 'N/A',
        )}
        src={data?.lead?.profile_picture}
        size="xs"
        shape="circle"
        color="primary"
      />
      <span className="my-auto underline text-blue-500">
        {leadDisplay.kind === 'name'
          ? leadDisplay.value
          : t(leadDisplay.value)}
      </span>
    </>
  );
  const leadLabelClassName = 'ls-lead-assigned ml-2 col-span-2 flex flex-row gap-x-2 my-auto';

  return (
    <div
      className={`ls-event grid grid-cols-11 rounded-sm ${eventTypeColor()}`}
    >
      <div className="ls-title flex flex-row gap-x-2  col-span-2">
        <span className={`ls-bar w-2 h-full ${eventDetail()}`}></span>
        <div className="my-auto flex flex-row px-1">
          <span
            className={`ls-dot w-2 h-2 rounded-full my-auto ${eventDetail()}`}
          />
          <div className="ml-2 my-auto block truncate w-40 pr-2">
            {data.title}
          </div>
        </div>
      </div>

      <div className="ls-event-type ml-2 col-span-2 my-auto flex flex-row">
        <div className="my-auto">{getTemplateNameType()}</div>
      </div>
      {leadId ? (
        <LeadChatLink chat={{ leadId }} className={leadLabelClassName}>
          {leadLabel}
        </LeadChatLink>
      ) : (
        <div className={leadLabelClassName}>{leadLabel}</div>
      )}
      <button
        className="ls-reminder col-span-2 ml-2 text-blue-400 my-auto  disabled:cursor-not-allowed"
        disabled={!shouldShowReminderButton || isEventDateAfterCurrent}
        onClick={() => sendReminder(event)}
      >
        {isLoading ? <Loading /> : t('agenda/agenda:send-notification')}
      </button>

      <div className="ls-actions flex flex-row gap-x-2 ml-2 col-span-1 my-auto">
        <PencilIcon
          onClick={() => {
            if (isEventDateAfterCurrent) return;

            setCurrentArg(arg);
            setShowUpdateEvent(true);
          }}
          className={`${
            isEventDateAfterCurrent
              ? 'cursor-not-allowed'
              : 'text-primary cursor-pointer'
          } w-5 h-5  `}
        />

        <TrashIcon
          onClick={() => {
            if (isEventDateAfterCurrent) return;

            setCurrentArg(arg);
            setShowDeleteEvent(true);
          }}
          className={`${
            isEventDateAfterCurrent
              ? 'cursor-not-allowed'
              : 'text-error cursor-pointer'
          } w-5 h-5  `}
        />
      </div>

      {leadId && (
        <div className="ls-actions flex flex-row gap-x-2 ml-2 col-span-1 my-auto">
          <Button size="sm" color="primary" onClick={openChat}>
            Abrir Chat
          </Button>
        </div>
      )}
    </div>
  );
}
